import type { StrokePoints } from "../handwriting/stroke-shape.js";
import { drawPad } from "./handwriting-drawing.js";

/**
 * The square a reader writes a character in.
 *
 * A `<canvas>`, the strokes drawn on it, and the pointer events that put them
 * there. What the strokes mean is `handwriting-lookup.ts`'s business and what
 * surrounds the square is `handwriting-panel.ts`'s. This is the surface.
 *
 * **Pointer events, so a finger, a stylus and a mouse are one code path.**
 * `pointerdown` through `pointerup` covers all three, and the capture taken on
 * the way down is what lets a stroke that leaves the square finish properly
 * rather than stopping at the edge. One pointer draws at a time, and the rest
 * are ignored until it lifts. See `#pointer`. `touch-action: none` in
 * `modules/handwriting.scss` is the other half of that. Without it a phone
 * treats the first movement as a scroll and the reader draws nothing.
 *
 * **The touches are refused as well, which is what stops iOS selecting text.**
 * `user-select: none` on the square and on the 复习 card was not enough, and
 * neither is `preventDefault` on a pointer event, which WebKit does not read
 * for selection. Its long press and its drag to select are touch gestures, and
 * a stroke that paused or ran off the square started one on the nearest text
 * outside it: the drill's instructions, its stage names, the card's buttons.
 * Cancelling `touchstart` and `touchmove` on the canvas takes the gesture away
 * before it starts and leaves the pointer events alone, since they are sent
 * first. The listeners have to say `passive: false`, or the browser may ignore
 * the cancel. A selection already on the page is let go when a stroke begins.
 *
 * **The canvas is sized in device pixels and drawn in CSS pixels.** A phone at
 * three device pixels to one CSS pixel draws a blurred stroke otherwise, and a
 * character a reader cannot see the shape of is one they cannot correct.
 */

/** What the pad tells its owner about. */
export interface HandwritingPadProperties {
  /** The canvas to draw on, which the panel has already put on the page. */
  readonly canvas: HTMLCanvasElement;

  /** Called whenever the strokes change, by a stroke arriving or leaving. */
  readonly onChange: () => void;
}

/**
 * How far a pointer has to move before the stroke gets another point.
 *
 * In CSS pixels. A pointer that has not moved this far adds nothing, which
 * keeps a slow careful stroke from arriving as two hundred points a millimetre
 * apart. The corner detection in `stroke-shape.ts` is unaffected either way,
 * and the redraw on every move is not.
 */
const minimumStep = 1.5;

/** The square a reader writes in. */
export class HandwritingPad {
  readonly #canvas: HTMLCanvasElement;
  readonly #onChange: () => void;
  readonly #strokes: StrokePoints[] = [];
  #drawing: [number, number][] | undefined;

  /**
   * Which pointer is drawing the stroke in progress.
   *
   * A phone sends an event per finger, and a reader writing with one hand rests
   * the other on the screen often enough to matter. Without this, a second
   * finger landing threw away the stroke under the first and every move it sent
   * extended the wrong one.
   */
  #pointer: number | undefined;

  /**
   * Whether the pad refuses new strokes. A locked pad draws nothing and tells
   * its owner nothing, so a pointer on it is ignored, and a stroke still being
   * drawn when it locks is dropped as it ends. The strokes already on
   * it stay, and Undo and Clear still take them back.
   */
  locked = false;

  constructor(properties: HandwritingPadProperties) {
    this.#canvas = properties.canvas;
    this.#onChange = properties.onChange;

    this.#canvas.addEventListener("pointerdown", (event) => {
      this.#begin(event);
    });
    this.#canvas.addEventListener("pointermove", (event) => {
      this.#extend(event);
    });
    this.#canvas.addEventListener("pointerup", (event) => {
      this.#end(event);
    });
    this.#canvas.addEventListener("pointercancel", (event) => {
      this.#end(event);
    });

    for (const type of ["touchstart", "touchmove"]) {
      this.#canvas.addEventListener(
        type,
        (event) => {
          event.preventDefault();
        },
        { passive: false },
      );
    }
  }

  /** Every stroke drawn so far, in the order they were drawn. */
  get strokes(): readonly StrokePoints[] {
    return this.#strokes;
  }

  /** Whether the reader has drawn anything. */
  get isEmpty(): boolean {
    return this.#strokes.length === 0 && this.#drawing === undefined;
  }

  /**
   * Matches the canvas to the box the page has given it, and redraws.
   *
   * Called when the pad is opened and whenever the window changes size. The
   * strokes are kept in CSS pixels, so a pad that has changed size redraws the
   * same strokes at the same coordinates in a larger square. That is a stroke
   * drawn small and left small, which is honest: the description scales
   * whatever it is given, so nothing about the lookup notices.
   */
  resize(): void {
    const box = this.#canvas.getBoundingClientRect();
    const ratio = this.#canvas.ownerDocument.defaultView?.devicePixelRatio ?? 1;

    this.#canvas.width = Math.max(1, Math.round(box.width * ratio));
    this.#canvas.height = Math.max(1, Math.round(box.height * ratio));
    this.#redraw();
  }

  /** Takes back the last stroke. */
  undo(): void {
    if (this.#strokes.pop() !== undefined) {
      this.#redraw();
      this.#onChange();
    }
  }

  /** Takes back every stroke. */
  clear(): void {
    if (this.#strokes.length === 0) {
      return;
    }

    this.#strokes.length = 0;
    this.#redraw();
    this.#onChange();
  }

  /** Where a pointer is, in the canvas's own CSS pixels. */
  #pointAt(event: PointerEvent): [number, number] {
    const box = this.#canvas.getBoundingClientRect();

    return [event.clientX - box.left, event.clientY - box.top];
  }

  /**
   * Starts a stroke, unless one is already being drawn.
   *
   * The second finger is ignored rather than taking the stroke over. A reader
   * halfway through a 竖 keeps their 竖. A locked pad starts nothing.
   */
  #begin(event: PointerEvent): void {
    if (this.locked || this.#pointer !== undefined) {
      return;
    }

    event.preventDefault();
    this.#canvas.ownerDocument.getSelection()?.removeAllRanges();
    this.#canvas.setPointerCapture(event.pointerId);
    this.#pointer = event.pointerId;
    this.#drawing = [this.#pointAt(event)];
    this.#redraw();
  }

  #extend(event: PointerEvent): void {
    const stroke = this.#drawing;

    if (stroke === undefined || event.pointerId !== this.#pointer) {
      return;
    }

    event.preventDefault();

    const point = this.#pointAt(event);
    const last = stroke[stroke.length - 1] as [number, number];

    if (Math.hypot(point[0] - last[0], point[1] - last[1]) < minimumStep) {
      return;
    }

    stroke.push(point);
    this.#redraw();
  }

  /**
   * Finishes the stroke in progress.
   *
   * A stroke of one point is kept. That is a 点 tapped rather than flicked, and
   * `characterShape` describes it as a run of no length in the place it was
   * tapped, which is what the outlines record for the same stroke.
   */
  #end(event: PointerEvent): void {
    const stroke = this.#drawing;

    if (stroke === undefined || event.pointerId !== this.#pointer) {
      return;
    }

    this.#drawing = undefined;
    this.#pointer = undefined;

    // A stroke begun before the pad locked ends after it, and is dropped.
    if (!this.locked) {
      this.#strokes.push(stroke);
    }

    this.#redraw();

    if (!this.locked) {
      this.#onChange();
    }
  }

  #redraw(): void {
    drawPad({
      canvas: this.#canvas,
      strokes: this.#strokes,
      drawing: this.#drawing,
    });
  }
}
